import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardPreview,
  Text,
  Button,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { MessageBar } from '../Common/ScrollableMessageBar';
import { ShieldCheckmarkRegular, PersonRegular } from '@fluentui/react-icons';
import { useMSALAuth } from '../../contexts/MSALAuthContext';
import { ElementButton, EventButton, PageName } from '../../tracking/tracking.types';
import useEventTracker from '../../hooks/useEventTracker';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  loginCard: {
    width: '380px',
    padding: '32px',
    boxShadow: tokens.shadow16,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
  },
  logoSection: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  icon: {
    fontSize: '48px',
    color: tokens.colorBrandForeground1,
  },
  title: {
    textAlign: 'center',
    marginBottom: '8px',
  },
  subtitle: {
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
    marginBottom: '24px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
});

export const MSALLogin: React.FC = () => {
  const styles = useStyles();
  const { login, isLoading } = useMSALAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const eventTracker = useEventTracker();
  const pageLoadStartTime = useRef<number>(performance.now());
  const hasTrackedPageLoad = useRef<boolean>(false);

  useEffect(() => {
    if (!hasTrackedPageLoad.current && eventTracker.isUsageTrackingInitialized && !isLoading) {
      const loadTimeMs = Math.round(performance.now() - pageLoadStartTime.current);
      eventTracker.eventTrackOnPageLoaded({
        pageName: PageName.LOGIN,
        loadTimeMs,
      });
      hasTrackedPageLoad.current = true;
    }
  }, [eventTracker.isUsageTrackingInitialized, isLoading]);

  const handleLogin = async () => {
    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.LOGIN,
      elementName: ElementButton.LOGIN,
      elementType: 'button',
    });

    setIsSubmitting(true);
    setError(null);

    try {
      await login();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state during authentication
  if (isLoading) {
    return (
      <div className={styles.container} data-testid="auth-msal-login-loading-page">
        <Card className={styles.loginCard} data-testid="auth-msal-login-loading-card">
          <CardPreview data-testid="auth-msal-login-loading-preview">
            <div style={{ textAlign: 'center', padding: tokens.spacingVerticalXL }} data-testid="auth-msal-login-loading-content">
              <Spinner size="large" label="Loading authentication..." data-testid="auth-msal-login-loading-spinner" />
            </div>
          </CardPreview>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="auth-msal-login-page">
      <Card className={styles.loginCard} data-testid="auth-msal-login-card">
        <div className={styles.logoSection} data-testid="auth-msal-login-logo-section">
          <ShieldCheckmarkRegular className={styles.icon} data-testid="auth-msal-login-shield-icon" />
        </div>

        <Text as="h1" size={600} weight="semibold" className={styles.title} data-testid="auth-msal-login-title">
          Automated Domain Management (ADM)
        </Text>

        <Text size={300} className={styles.subtitle} data-testid="auth-msal-login-subtitle">
          Sign in to manage Teams External Access policies.
        </Text>

        <div className={styles.form} data-testid="auth-msal-login-form">
          {error && (
            <MessageBar intent="error" data-testid="auth-msal-login-error-messagebar">
              {error}
            </MessageBar>
          )}

          <Button
            appearance="primary"
            size="large"
            disabled={isSubmitting}
            icon={isSubmitting ? <Spinner size="tiny" data-testid="auth-msal-login-sign-in-spinner" /> : <PersonRegular />}
            onClick={handleLogin}
            style={{ width: '100%' }}
            data-testid="auth-msal-login-sign-in-button"
          >
            {isSubmitting ? 'Signing in...' : 'Sign in with Microsoft'}
          </Button>
        </div>
      </Card>
    </div>
  );
};